/**
 * Centralized phone number utility for validation, formatting, and link generation.
 */

// Dummy/placeholder patterns commonly entered during seeding or mock testing
const DUMMY_PHONE_PATTERNS = [
  /^0+$/,
  /^1+$/,
  /^9+$/,
  /^1234567890$/,
  /^9876543210$/,
  /9812012345/, // Seeded placeholder pattern in Fatehabad
  /9996666666/, // Seeded placeholder pattern in Fatehabad
  /9896300000/, // Seeded placeholder pattern in Fatehabad
  /^(.)\1{7,}$/  // Any 8+ repeated identical digits
];

/**
 * Checks if a phone number string is valid and not a placeholder/dummy.
 * @param {string} phone
 * @returns {boolean}
 */
export function isValidPhone(phone) {
  if (!phone || typeof phone !== 'string') return false;

  const trimmed = phone.trim();
  if (!trimmed || trimmed.toLowerCase() === 'n/a' || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
    return false;
  }

  // Extract pure digits
  const cleanDigits = trimmed.replace(/\D/g, '');

  // Must have at least 10 digits (or 8-11 for Indian landlines with STD)
  if (cleanDigits.length < 10) {
    return false;
  }

  // Check against dummy patterns
  for (const pattern of DUMMY_PHONE_PATTERNS) {
    if (pattern.test(cleanDigits) || pattern.test(trimmed)) {
      return false;
    }
  }

  return true;
}

/**
 * Normalizes phone number into clean dialable string for `tel:` links.
 * @param {string} phone
 * @returns {string}
 */
export function getTelLink(phone) {
  if (!isValidPhone(phone)) return '';
  const cleanDigits = phone.replace(/\D/g, '');
  if (cleanDigits.length === 10) {
    return `tel:+91${cleanDigits}`;
  }
  if (cleanDigits.startsWith('91') && cleanDigits.length === 12) {
    return `tel:+${cleanDigits}`;
  }
  if (cleanDigits.startsWith('0')) {
    return `tel:${cleanDigits}`;
  }
  return `tel:${phone.trim()}`;
}

/**
 * Generates WhatsApp chat link (`https://wa.me/91...`) for valid mobile numbers.
 * @param {string} phone
 * @returns {string}
 */
export function getWhatsAppLink(phone) {
  if (!isValidPhone(phone)) return '';
  const cleanDigits = phone.replace(/\D/g, '');

  let mobileDigits = cleanDigits;
  if (cleanDigits.startsWith('91') && cleanDigits.length === 12) {
    mobileDigits = cleanDigits.slice(2);
  } else if (cleanDigits.startsWith('0') && cleanDigits.length === 11) {
    mobileDigits = cleanDigits.slice(1);
  }

  // Indian mobile numbers typically start with 6, 7, 8, or 9
  if (mobileDigits.length === 10 && /^[6-9]/.test(mobileDigits)) {
    return `https://wa.me/91${mobileDigits}`;
  }

  return '';
}

/**
 * Formats a phone number for user-friendly display.
 * @param {string} phone
 * @returns {string}
 */
export function formatPhoneDisplay(phone) {
  if (!isValidPhone(phone)) return 'N/A';
  const clean = phone.trim();

  // If already cleanly formatted with STD code (e.g. 01667-223100), return as is
  if (/^0\d{3,4}-\d{6,8}$/.test(clean)) {
    return clean;
  }

  const cleanDigits = clean.replace(/\D/g, '');
  if (cleanDigits.length === 10) {
    return `${cleanDigits.slice(0, 5)} ${cleanDigits.slice(5)}`;
  }
  if (cleanDigits.startsWith('91') && cleanDigits.length === 12) {
    return `+91 ${cleanDigits.slice(2, 7)} ${cleanDigits.slice(7)}`;
  }

  return clean;
}
